/**
 * Google blocks OAuth sign-in from embedded in-app browsers (webviews) with
 * "disallowed_useragent" / "This browser or app may not be secure", while
 * Facebook and Apple allow them. When we detect such a browser we intercept
 * the Google button click and offer to reopen the page in the system browser
 * instead of starting an OAuth flow that Google will reject.
 *
 * Detection covers ANY in-app browser, not a single app, via two generic
 * webview signals plus a token list for apps the generic signals can miss:
 *  - iOS: every WKWebView-embedded browser (LinkedIn, Instagram, Facebook,
 *    Slack, Claude iOS, ...) ships a WebKit UA WITHOUT the `Safari/` token —
 *    real Safari, Chrome/Firefox on iOS and SFSafariViewController (which
 *    Google allows) all carry it.
 *  - Android: the system WebView appends the standard `; wv)` token.
 *  - Token list: in-app browsers that mask the generic signals (mostly
 *    Android apps whose webview drops `wv`) and desktop app shells (e.g. the
 *    Claude desktop app's embedded browser — Electron-based).
 *
 * Note: FBAV/FBAN/Instagram/musical_ly/Twitter UAs are ALSO covered by the
 * older, blunter `UNSUPPORTED_AGENTS_FOR_SOCIAL_AUTH` gate (socialAuth store),
 * which hides all social buttons on mobile; where that gate applies there is
 * no Google button to click, so the two mechanisms don't conflict.
 */
const IN_APP_BROWSER_UA_TOKENS = [
  'LinkedInApp',
  'FBAN', // Facebook iOS
  'FBAV', // Facebook app
  'FB_IAB', // Facebook Android in-app browser
  'Instagram',
  'musical_ly', // TikTok
  'BytedanceWebview',
  'Snapchat',
  'Pinterest',
  'MicroMessenger', // WeChat
  'Line/',
  'Electron/', // desktop app shells (e.g. the Claude desktop app)
  'Claude/',
];

const isIosWebView = (ua: string): boolean =>
  /iPhone|iPad|iPod/i.test(ua) &&
  /AppleWebKit/i.test(ua) &&
  !/Safari\//i.test(ua);

const isAndroidWebView = (ua: string): boolean =>
  /Android/i.test(ua) && /;\s?wv\)/.test(ua);

export const isGoogleBlockedInAppBrowser = (): boolean => {
  const ua = navigator?.userAgent ?? '';
  return (
    IN_APP_BROWSER_UA_TOKENS.some((token) => ua.includes(token)) ||
    isIosWebView(ua) ||
    isAndroidWebView(ua)
  );
};

const isAndroid = (): boolean => /android/i.test(navigator?.userAgent ?? '');

const isIos = (): boolean =>
  /iPhone|iPad|iPod/i.test(navigator?.userAgent ?? '');

/**
 * Builds a URL that hands the given https page off to the OS default browser,
 * escaping the in-app webview. Returns null when no escape scheme exists for
 * the current platform (e.g. desktop).
 *
 * These schemes are best-effort: `intent://` is honored by Chrome / Chrome
 * Custom Tabs (the `browser_fallback_url` lets them recover when no app claims
 * the intent) but not by every bare Android WebView; `x-safari-` is an
 * undocumented iOS trick that many webviews ignore. Callers must therefore
 * always copy the link too (see `copyPageLink`) so the user is never stuck.
 */
export const getExternalBrowserUrl = (url: string): string | null => {
  if (isAndroid()) {
    const bareUrl = url.replace(/^https?:\/\//, '');
    const fallback = encodeURIComponent(url);
    return `intent://${bareUrl}#Intent;scheme=https;S.browser_fallback_url=${fallback};end;`;
  }
  if (isIos()) {
    return `x-safari-${url}`;
  }
  return null;
};

/**
 * Best-effort attempt to reopen `url` (default: the current page, with all its
 * query params) in the system browser. Returns false when the platform has no
 * escape scheme; true only means the attempt was made, NOT that it succeeded —
 * so callers must pair it with `copyPageLink`.
 */
export const openInExternalBrowser = (
  url: string = window.location.href,
): boolean => {
  const externalUrl = getExternalBrowserUrl(url);
  if (!externalUrl) {
    return false;
  }
  window.location.href = externalUrl;
  return true;
};

/**
 * Guaranteed fallback: copies the current page link to the clipboard so the
 * user can paste it into a real browser even when the escape scheme above is a
 * no-op in their webview. Uses the async Clipboard API where available and a
 * hidden-textarea `execCommand('copy')` fallback for older webviews.
 */
export const copyPageLink = async (
  url: string = window.location.href,
): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // fall through to the legacy path below
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
